// [PROPOSITION-1] Modale de création de proposition — owner side
import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Search, Package, Minus, Plus, Loader2, ChevronRight, Tag
} from "lucide-react";
import { toast } from "sonner";

export default function PropositionModal({ open, onClose, userId, convId, produitsDiscutes, pendingProductIds = [], onCreated, accent, devise }) {
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Step 2 state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedTaille, setSelectedTaille] = useState(null);
  const [quantite, setQuantite] = useState(1);
  const [prixPropose, setPrixPropose] = useState("");
  const [note, setNote] = useState("");

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/conversations/${userId}/products/search?q=${searchQ}`, getAuthHeaders());
      setProducts(res.data.products || []);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, [userId, searchQ, getAuthHeaders]);

  useEffect(() => { if (open) fetchProducts(); }, [open, fetchProducts]);
  useEffect(() => {
    if (open) { setStep(1); setSelectedProduct(null); setSelectedTaille(null); setQuantite(1); setPrixPropose(""); setNote(""); }
  }, [open]);

  const selectProduct = (p) => {
    setSelectedProduct(p);
    setSelectedTaille(null);
    setQuantite(1);
    setPrixPropose(String(p.price || ""));
    setNote("");
    setStep(2);
  };

  const selectedVariant = selectedProduct?.variants?.find(v => v.size === selectedTaille);
  const maxQty = selectedVariant?.stockDispo || 0;
  const prixInitial = selectedProduct?.price || 0;
  const prixNum = parseFloat(prixPropose) || 0;
  const diff = prixInitial - prixNum;
  const diffPct = prixInitial > 0 ? Math.round((diff / prixInitial) * 100) : 0;

  const handleSubmit = async () => {
    if (!selectedProduct || !selectedTaille || !prixNum || quantite < 1) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${userId}/${convId}/proposition`, {
        produitId: selectedProduct.id,
        taille: selectedTaille,
        couleur: selectedVariant?.color_name || null,
        prixPropose: prixNum,
        quantite,
        note: note || null
      }, getAuthHeaders());
      toast.success("Proposition envoyée !");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  if (!open) return null;

  // Sort: discussed products first
  const discussed = new Set(produitsDiscutes || []);
  const sorted = [...products].sort((a, b) => {
    const aD = discussed.has(a.id) ? 0 : 1;
    const bD = discussed.has(b.id) ? 0 : 1;
    return aD - bD;
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}
      >
        <motion.div
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
          data-testid="proposition-modal"
          style={{
            width: "100%", maxWidth: 480, maxHeight: "85vh",
            background: "#1A1A1A", borderRadius: "20px 20px 0 0",
            padding: "28px 24px", display: "flex", flexDirection: "column",
            border: "1px solid #2A2A2A", borderBottom: "none",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {step === 2 && (
                <button onClick={() => setStep(1)} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", padding: 2 }}>
                  <ChevronRight size={18} style={{ transform: "rotate(180deg)" }} />
                </button>
              )}
              <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>
                {step === 1 ? "Choisir un produit" : "Proposition de commande"}
              </h3>
            </div>
            <button onClick={onClose} data-testid="close-proposition-modal"
              style={{ background: "none", border: "none", color: "#6B7280", cursor: "pointer", padding: 4 }}>
              <X size={20} />
            </button>
          </div>

          {/* Step 1: Product Selection */}
          {step === 1 && (
            <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Search */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "#0F0F0F", borderRadius: 10, padding: "10px 14px",
                border: "1px solid #2A2A2A",
              }}>
                <Search size={15} style={{ color: "#4B5563", flexShrink: 0 }} />
                <input
                  data-testid="proposition-search"
                  value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  placeholder="Rechercher un produit..."
                  style={{ background: "none", border: "none", color: "#fff", fontSize: 13, outline: "none", flex: 1, fontFamily: "inherit" }}
                />
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: 30 }}><Loader2 size={20} className="animate-spin" style={{ color: accent }} /></div>
              ) : (
                <div style={{ flex: 1, overflow: "auto" }}>
                  {sorted.map(p => {
                    const totalDispo = p.variants?.reduce((s, v) => s + (v.stockDispo || 0), 0) || 0;
                    const isOut = totalDispo === 0;
                    const hasPending = pendingProductIds.includes(p.id);
                    const isDisabled = isOut || hasPending;
                    const isDiscussed = discussed.has(p.id);
                    const imgSrc = p.image?.startsWith("data:") ? p.image : p.image?.startsWith("http") ? p.image : p.image ? `data:image/jpeg;base64,${p.image}` : null;
                    return (
                      <div key={p.id}
                        data-testid={`prop-product-${p.id}`}
                        onClick={() => !isDisabled && selectProduct(p)}
                        style={{
                          display: "flex", alignItems: "center", gap: 12,
                          padding: "10px 12px", borderRadius: 10, cursor: isDisabled ? "default" : "pointer",
                          opacity: isDisabled ? 0.4 : 1,
                          background: isDiscussed ? "rgba(255,255,255,0.03)" : "transparent",
                          border: isDiscussed ? `1px solid ${accent}33` : "1px solid transparent",
                          transition: "background 150ms", marginBottom: 4,
                        }}
                        onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isDiscussed ? "rgba(255,255,255,0.03)" : "transparent"; }}
                      >
                        {imgSrc ? (
                          <img src={imgSrc} alt={p.name} style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 48, height: 48, borderRadius: 8, background: "#2A2A2A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Package size={20} style={{ color: "#4B5563" }} />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ color: "#fff", fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                            {isDiscussed && <span style={{ fontSize: 10, color: accent, fontWeight: 600, flexShrink: 0 }}>discuté</span>}
                          </div>
                          <div style={{ color: isOut ? "#EF4444" : hasPending ? "#F59E0B" : "#9CA3AF", fontSize: 12, marginTop: 2 }}>
                            {isOut ? "Rupture de stock" : hasPending ? "Proposition en attente" : `${totalDispo} en stock`}
                          </div>
                        </div>
                        <span style={{ color: "#F97316", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                          {p.price?.toLocaleString("fr-FR")}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Proposal Details */}
          {step === 2 && selectedProduct && (
            <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Selected product header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {(() => {
                  const img = selectedProduct.image;
                  const src = img?.startsWith("data:") ? img : img?.startsWith("http") ? img : img ? `data:image/jpeg;base64,${img}` : null;
                  return src ? (
                    <img src={src} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 64, height: 64, borderRadius: 10, background: "#2A2A2A", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Package size={24} style={{ color: "#4B5563" }} />
                    </div>
                  );
                })()}
                <div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{selectedProduct.name}</div>
                  <div style={{ color: "#6B7280", fontSize: 12, marginTop: 2 }}>{selectedProduct.category}</div>
                </div>
              </div>

              {/* Taille pills */}
              <div>
                <label style={{ color: "#9CA3AF", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 8 }}>Taille</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {selectedProduct.variants?.map(v => (
                    <button key={v.size}
                      data-testid={`taille-${v.size}`}
                      onClick={() => { if (v.stockDispo > 0) { setSelectedTaille(v.size); setQuantite(1); } }}
                      style={{
                        padding: "8px 16px", borderRadius: 8,
                        background: selectedTaille === v.size ? accent : "rgba(255,255,255,0.04)",
                        border: selectedTaille === v.size ? "none" : "1px solid #2A2A2A",
                        color: v.stockDispo === 0 ? "#4B5563" : selectedTaille === v.size ? "#fff" : "#D1D5DB",
                        fontSize: 13, fontWeight: 600, cursor: v.stockDispo > 0 ? "pointer" : "default",
                        opacity: v.stockDispo === 0 ? 0.4 : 1, transition: "all 150ms",
                      }}
                    >
                      {v.size} <span style={{ fontSize: 11, opacity: 0.7 }}>({v.stockDispo})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantité */}
              {selectedTaille && (
                <div>
                  <label style={{ color: "#9CA3AF", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 8 }}>Quantité</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button onClick={() => setQuantite(q => Math.max(1, q - 1))}
                      style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid #2A2A2A", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Minus size={14} />
                    </button>
                    <span data-testid="prop-quantity" style={{ color: "#fff", fontSize: 18, fontWeight: 700, minWidth: 30, textAlign: "center" }}>{quantite}</span>
                    <button onClick={() => setQuantite(q => Math.min(maxQty, q + 1))}
                      style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid #2A2A2A", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Plus size={14} />
                    </button>
                    <span style={{ color: "#6B7280", fontSize: 12 }}>Max: {maxQty}</span>
                  </div>
                </div>
              )}

              {/* Prix initial barré */}
              <div>
                <span style={{ color: "#6B7280", fontSize: 12, textDecoration: "line-through" }}>
                  Prix boutique : {prixInitial.toLocaleString("fr-FR")} {selectedProduct.variants?.[0]?.currency || ""}
                </span>
              </div>

              {/* Prix proposé */}
              <div>
                <label style={{ color: "#9CA3AF", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>Votre prix proposé</label>
                <input
                  data-testid="prop-prix"
                  type="number"
                  value={prixPropose}
                  onChange={e => setPrixPropose(e.target.value)}
                  style={{
                    width: "100%", background: "#0F0F0F", border: "1px solid #2A2A2A",
                    borderRadius: 10, padding: "12px 14px", color: accent,
                    fontSize: 18, fontWeight: 700, outline: "none", fontFamily: "inherit",
                    transition: "border-color 200ms",
                  }}
                  onFocus={e => e.target.style.borderColor = accent}
                  onBlur={e => e.target.style.borderColor = "#2A2A2A"}
                />
                {prixNum > 0 && diff !== 0 && (
                  <div style={{
                    marginTop: 6, padding: "4px 10px", borderRadius: 6, display: "inline-block",
                    background: diff > 0 ? "rgba(34,197,94,0.1)" : "rgba(249,115,22,0.1)",
                    color: diff > 0 ? "#22C55E" : "#F97316",
                    fontSize: 12, fontWeight: 600,
                  }}>
                    {diff > 0 ? `Remise : -${diff.toLocaleString("fr-FR")} (-${diffPct}%)` : `Prix majoré : +${Math.abs(diffPct)}%`}
                  </div>
                )}
              </div>

              {/* Note */}
              <div>
                <label style={{ color: "#9CA3AF", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>Note (optionnel)</label>
                <textarea
                  data-testid="prop-note"
                  value={note}
                  onChange={e => { if (e.target.value.length <= 150) setNote(e.target.value); }}
                  placeholder="Ex: Remise fidélité, offre spéciale, lot..."
                  rows={2}
                  style={{
                    width: "100%", background: "#0F0F0F", border: "1px solid #2A2A2A",
                    borderRadius: 10, padding: "10px 14px", color: "#fff", fontSize: 13,
                    outline: "none", resize: "none", fontFamily: "inherit",
                  }}
                />
                <div style={{ color: "#4B5563", fontSize: 10, textAlign: "right" }}>{note.length}/150</div>
              </div>

              {/* Recap */}
              {selectedTaille && prixNum > 0 && (
                <div style={{
                  background: "rgba(255,255,255,0.03)", border: "1px solid #2A2A2A",
                  borderRadius: 12, padding: "14px 16px",
                }}>
                  <div style={{ color: "#D1D5DB", fontSize: 13, fontWeight: 600 }}>
                    {selectedProduct.name} — Taille {selectedTaille}
                  </div>
                  <div style={{ color: "#9CA3AF", fontSize: 12, marginTop: 4 }}>Qté : {quantite}</div>
                  <div style={{ color: "#F97316", fontWeight: 800, fontSize: 16, marginTop: 6 }}>
                    Prix proposé : {(prixNum * quantite).toLocaleString("fr-FR")}
                  </div>
                  {diff > 0 && (
                    <div style={{ color: "#22C55E", fontSize: 12, marginTop: 2 }}>
                      Remise : -{(diff * quantite).toLocaleString("fr-FR")} (-{diffPct}%)
                    </div>
                  )}
                </div>
              )}

              {/* Submit */}
              <button
                data-testid="submit-proposition-btn"
                onClick={handleSubmit}
                disabled={!selectedTaille || !prixNum || quantite < 1 || sending}
                style={{
                  width: "100%", padding: 14, borderRadius: 12,
                  background: (!selectedTaille || !prixNum) ? "#2A2A2A" : `linear-gradient(135deg, ${accent}, ${accent}CC)`,
                  color: "#fff", border: "none", fontWeight: 700, fontSize: 14,
                  cursor: (!selectedTaille || !prixNum) ? "default" : "pointer",
                  opacity: (!selectedTaille || !prixNum || sending) ? 0.5 : 1,
                  transition: "all 200ms", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  boxShadow: (selectedTaille && prixNum) ? `0 4px 16px ${accent}33` : "none",
                }}
              >
                {sending ? (
                  <><Loader2 size={16} className="animate-spin" /> Envoi en cours...</>
                ) : (
                  <>
                    <Tag size={15} />
                    Envoyer la proposition
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
